import type { Tour, TourFrame } from '@carboncopyco/types';
import { Debug } from '@carboncopyco/utils';
import { fileServiceApi } from './fileServiceApi';
Debug.extend('demostack-data:api:tourFilesApi');

export interface UploadToursFileResponse {
  url_to_json: string;
}
export interface UploadToursFileArgs {
  organizationId: string;
  tourId: TourFrame['id'];
  jsonId: string;
  token?: Tour['fileServiceToken'];
  frames: any;
}

export interface UploadToursResourceResponse {
  url_to_resource: string;
}

export interface UploadToursResourceArgs {
  organizationId: string;
  tourId: TourFrame['id'];
  resourceId: string;
  token?: Tour['fileServiceToken'];
  fileData: any;
}

export interface UploadToursScreenshotResponse {
  path_to_image: string;
}
export interface UploadToursScreenshotArgs {
  baseUrl: string;
  organizationId: string;
  tourId: TourFrame['id'];
  screenshotId: string;
  token?: Tour['fileServiceToken'];
  fileData: any;
}

export interface UploadToursFileDirectUploadResponse {
  upload_url: string;
  file_service_url: string;
}

export interface UploadToursFileDirectUploadArgs {
  organizationId: string;
  token?: Tour['fileServiceToken'];
  resourceId: string;
}

const tourFilesApi = fileServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    uploadToursFile: builder.mutation<
      UploadToursFileResponse,
      UploadToursFileArgs
    >({
      query: ({ organizationId, tourId, jsonId, token, frames }) => {
        const file = new Blob([frames], {
          type: 'application/json',
        });
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/files/tours/${organizationId}/tour/${tourId}/json/${jsonId}`,
          params: { token },
          method: 'POST',
          body: formData,
        };
      },
    }),

    uploadToursResource: builder.mutation<
      UploadToursResourceResponse,
      UploadToursResourceArgs
    >({
      query: ({ organizationId, tourId, resourceId, token, fileData }) => {
        const file = new File([fileData.arrayBuffer], resourceId, {
          type: fileData.contentType,
        });
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/files/tours/${organizationId}/tour/${tourId}/resource/${resourceId}`,
          params: { token },
          method: 'POST',
          body: formData,
        };
      },
    }),
    uploadToursScreenshot: builder.mutation<
      UploadToursScreenshotResponse,
      UploadToursScreenshotArgs
    >({
      query: ({
        baseUrl,
        organizationId,
        tourId,
        screenshotId,
        token,
        fileData,
      }) => {
        const file = new File([fileData.arrayBuffer], screenshotId, {
          type: fileData.contentType,
        });

        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `${baseUrl}/files/tours/${organizationId}/tour/${tourId}/screenshots/upload`,
          params: { token },
          method: 'POST',
          body: formData,
        };
      },
    }),
    uploadToursFileDirectUpload: builder.mutation<
      UploadToursFileDirectUploadResponse,
      UploadToursFileDirectUploadArgs
    >({
      query: ({ organizationId, token, resourceId }) => {
        return {
          url: `/files/tours/${organizationId}/${resourceId}/direct-upload`,
          params: { token },
          method: 'POST',
        };
      },
    }),
  }),
});

export const {
  useUploadToursFileMutation,
  useUploadToursResourceMutation,
  useUploadToursScreenshotMutation,
  useUploadToursFileDirectUploadMutation,
} = tourFilesApi;
